import { defaultWebDateRange } from 'components/DisasterGPT/chatV2/dataSourceSelections';
import { isAnalyticsSection } from 'components/DisasterGPT/constants/opordSectionDefaults';
import {
  collectSectionSubtreeIds,
  makeReportSectionEntry,
  selectableReportSections,
  type SectionOutlineLike,
} from './reportSectionSources';

export type DefaultSourcesSection = SectionOutlineLike & {
  type?: string;
  automation?: { provider?: string } | null;
  output_format_structured?: {
    automation?: { provider?: string } | null;
  } | null;
  section_versions?: Array<{
    data_string?: string | null;
    status?: string | null;
  }> | null;
};

function hasLayerResults(dataString: string | null | undefined): boolean {
  if (!dataString) return false;
  try {
    const parsed = JSON.parse(dataString) as {
      LayerQueryResults?: { layers?: unknown[]; error?: unknown };
    };
    const results = parsed?.LayerQueryResults;
    return (
      !!results &&
      !results.error &&
      Array.isArray(results.layers) &&
      results.layers.length > 0
    );
  } catch {
    return false;
  }
}

export function analyticsHasResults(
  sections: DefaultSourcesSection[],
  analyticsId: string
): boolean {
  const ids = collectSectionSubtreeIds(sections, analyticsId);
  return sections.some(
    (section) =>
      ids.has(section.id) &&
      (section.section_versions ?? []).some(
        (version) =>
          version?.status !== 'archived' &&
          hasLayerResults(version?.data_string)
      )
  );
}

export function defaultTextSectionDataSources(params: {
  reportSections?: DefaultSourcesSection[] | null;
  currentSectionId?: string | null;
  highSecurity: boolean;
}): Record<string, unknown>[] {
  const { reportSections, currentSectionId, highSecurity } = params;
  const defaults: Record<string, unknown>[] = highSecurity
    ? []
    : [
        {
          source: 'Web',
          description: 'Web Scraping',
          id: '',
          webDateRange: defaultWebDateRange(),
        },
      ];
  const sections = reportSections ?? [];
  const analytics = sections.find(
    (section) => !section.parent_section_id && isAnalyticsSection(section)
  );
  if (!analytics || !analyticsHasResults(sections, analytics.id)) {
    return defaults;
  }
  const selectable = selectableReportSections(sections, currentSectionId);
  const coversCurrent =
    !!currentSectionId &&
    collectSectionSubtreeIds(sections, analytics.id).has(currentSectionId);
  if (
    coversCurrent ||
    !selectable.some((section) => section.id === analytics.id)
  ) {
    return defaults;
  }
  return [...defaults, makeReportSectionEntry(analytics, true)];
}
